<template lang="pug">
.CustomSelect(
  :style='{ "font-size": fontSize + "px" }',
  @click='handleClick',
  v-click-outside='hide'
)
  .Select(:class='{ "Select--transparent": isTransparentHeader, "Select--error": hasError, "Select--warning": hasWarning }')
    span.Select-name.OneLine(:class='{ "Select-name--bold": isBoldSelected, "Select-name--icon": value && value.icon }')
      .Select-icon.Select-icon--bg(
        v-if='value && value.icon && value.icon.type === "color"',
        :style='{ background: value.icon.color }'
      )
      .Select-icon(v-if='value && value.icon && value.icon.type === "img"')
        img(:src='value.icon.file')

      //- Поле для поиска, отображаем если разрешен поиск
      input.Select-search-input(
        v-if='searchEnabled',
        ref='searchInput',
        :value='displayValue',
        @input='handleSearchInput'
      )
      //- Если поиск не разрешен, то отображаем обычный текст
      template(v-else)
        | {{ displayValue }}

      //- Плейсхолдер, который выводится при пустом значении
      template(v-if='!displayValue')
        .Select-placeholder(v-if='searchEnabled')
          | {{ placeholderText }}
        template(v-else)
          | {{ placeholderText }}

    span.Select-arrow(:class='{ "Select-arrow--grey": arrowColor === "grey" }')
      i.Icon.IconChevronDown

  ul.Options(
    v-if='showOptions',
    :style='{ width: dropdownWidth }',
    :class='{ "Options--right": toRight }'
  )
    li.Options-item.Options-item--empty(v-if='!filtredOptions || !filtredOptions.length') Список пуст
    li.Options-item(
      v-for='option in filtredOptions',
      :class='{ "Options-item--disabled": option.disabled, "Options-item--selected": option === value }',
      @click='selectOption(option)'
    )
      label(
        :class='{ "Label--icon": option.icon }',
        :id='optionsIdPrefix && option.id ? optionsIdPrefix + option.id : ""'
      )
        .Select-icon.Select-icon--bg(
          v-if='option.icon && option.icon.type === "color"',
          :style='{ background: option.icon.color }'
        )
        .Select-icon(v-if='option.icon && option.icon.type === "img"')
          img(:src='option.icon.file')

        input(
          type='radio',
          :value='option'
        )
        | {{ option.name }}
</template>

<script>
import ClickOutside from 'vue-click-outside'

export default {
  name: 'custom-select',

  directives: {
    ClickOutside
  },

  props: {
    options: Array,
    value: Object | null,
    dropdownWidth: {
      type: String,
      default: '100%'
    },
    fontSize: {
      type: Number,
      default: 14
    },
    optionsIdPrefix: {
      type: String,
      default: ''
    },
    arrowColor: {
      type: String,
      default: 'default'
    },
    isBoldSelected: Boolean,
    isTransparentHeader: Boolean,
    toRight: Boolean,
    placeholder: String,

    /**Status*/
    hasError: Boolean,
    hasWarning: Boolean,

    /**Включение возможности поиска */
    searchEnabled: Boolean
  },

  data() {
    return {
      showOptions: false,

      /**Значение, которое ввели в поисковую строку */
      searchString: null
    }
  },

  computed: {
    filtredOptions() {
      if (!this.searchEnabled || !this.searchString) {
        return this.options
      }

      return this.options.filter((option) => {
        return typeof option.name === 'string' && option.name.toUpperCase().includes(this.searchString.toUpperCase())
      })
    },

    displayValue() {
      //Если разрешен поиск и есть поисковая строка (даже пустая) отображаем поисковую строку
      if (this.searchEnabled && this.searchString !== null && this.showOptions) {
        return this.searchString
      }

      //Оторбражаем значение если оно есть
      if (this.value) {
        return this.value.name
      }

      return null
    },

    placeholderText() {
      return this.placeholder || 'Выберите'
    }
  },

  watch: {
    showOptions() {
      this.$emit('showOptionsChanged', this.showOptions)
    }
  },

  methods: {
    selectOption(option) {
      if (option.disabled) return
      if (!this.value || this.value.id !== option.id) {
        this.$emit('input', option)
      }
    },

    hide() {
      //Сбросить поиск при скрытии списка доступных значений
      this.searchString = null

      //Скрыть список доступных значений
      this.showOptions = false
    },

    handleClick() {
      this.showOptions = !this.showOptions

      //При открытии генерируем событие
      if (this.showOptions) {
        this.$emit('opening')
      }

      //При открытии списка доступных значений надо сделать активным поле поиска,
      //если поиск включен
      if (this.searchEnabled && this.$refs.searchInput && this.showOptions) {
        this.$refs.searchInput.focus()
      }
    },

    handleSearchInput(event) {
      this.searchString = event.target.value

      //При поиске надо отображать списко доступных значений
      this.showOptions = true
    }
  }
}
</script>

<style lang="scss">
@import '~base';

#app .CustomSelect,
.Popup .CustomSelect {
  --headerBorderBottom: #{$grey3};
  --arrowColor: #{$grey1};
  --optionsBorder: #{$grey3};
  --optionsBg: #{$white2};
  --optionsHoverBg: #{$grey3};
  --selectedBg: #{$grey7};
  --shadowColor: rgba(96, 104, 115, 0.2);
  --selectedBorder: transparent;
}

#app[data-theme='dark'] .CustomSelect,
.Popup[data-theme='dark'] .CustomSelect {
  --headerBorderBottom: #{$grey2};
  --arrowColor: #{$grey1};
  --optionsBorder: #{$black4};
  --optionsBg: #{$black3};
  --optionsHoverBg: #{$black2};
  --selectedBg: #{$black3};
  --shadowColor: transparent;
  --selectedBorder: #{$black4};
}
</style>

<style scoped lang="scss">
@import '~base';

.CustomSelect {
  position: relative;
  display: block;
  width: 100%;
  max-width: 100%;
  height: 100%;
  color: var(--basicText);
}

.Select {
  position: relative;
  display: flex;
  height: 100%;
  align-items: center;
  padding: 0 0.3rem 0 0.7rem;
  background: var(--selectedBg);
  border: 1px solid var(--selectedBorder);
  border-radius: 14px;

  &--error {
    border-color: var(--errorColor);
  }

  &--warning {
    border-color: var(--warningColor);
  }

  &-icon {
    position: absolute;
    left: 0px;
    top: 50%;
    width: 20px;
    height: 20px;
    margin-top: -10px;
    margin-right: 10px;
    border-radius: 2px;

    &--bg {
      margin-top: -3px;
      width: 6px;
      height: 6px;
    }

    img {
      display: block;
      max-width: 100%;
      max-height: 100%;
      margin-right: 5px;
    }
  }

  &--transparent {
    background: transparent;
    border: 0;
  }

  &-name {
    flex: 1 1 auto;
    position: relative;
    cursor: pointer;
    line-height: 26px;

    &--icon {
      padding-left: 22px;
    }

    &--bold {
      font-weight: 500;
    }
  }

  &-arrow {
    flex: 0 0 25px;
    width: 25px;
    font-weight: 400;
    text-align: center;
    color: var(--arrowColor);

    &--grey {
      color: var(--arrowColor);
    }
  }

  &-search-input {
    background-color: transparent;
    border: none;
    outline: none;
    width: 100%;
    padding: 0;
    margin: 0;
    z-index: 1;
  }

  &-placeholder {
    position: absolute;
    top: 0;
    width: 100%;
    pointer-events: none;
  }
}

.Options {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: 240px;
  padding: 0;
  border: 1px solid var(--optionsBorder);
  background: var(--optionsBg);
  overflow: auto;
  z-index: 1500;
  box-shadow: 0px 4px 4px var(--shadowColor);

  &--empty {
    padding: 15px;
    text-align: center;
    font-size: 12px;
  }

  &--right {
    right: 0;
    left: auto;
  }

  .Label--icon {
    position: relative;
    padding-left: 22px;
  }

  &-item {
    padding: 12px;
    line-height: 1.2;
    cursor: pointer;

    &--empty {
      pointer-events: none;
      text-align: center;
      cursor: default;
    }

    &:hover {
      background: var(--optionsHoverBg);
    }

    &--disabled {
      opacity: 0.5;
      cursor: default;

      &:hover {
        background: var(--optionsBg);
      }
    }

    &--selected {
      background-color: var(--selectedBg);
    }

    &:last-child {
      border-bottom: none;
    }
    input {
      display: none;
    }
    label {
      display: flex;
      margin: 0;
      padding: 0;
      cursor: inherit;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/CustomSelect.vue